import { z } from 'zod';

/**
 * Regular expression pattern for safe icon IDs.
 * Only allows alphanumeric characters, hyphens, and underscores.
 * This prevents injection attacks while allowing flexibility for icon naming.
 */
export const SAFE_ICON_PATTERN = /^[a-zA-Z0-9\-_]+$/;

/**
 * Regular expression pattern for safe CSS linear gradients.
 * Only allows: linear-gradient with direction (135deg or 90deg) and hex colors.
 * Example: linear-gradient(135deg, #667eea 0%, #764ba2 100%)
 */
export const SAFE_GRADIENT_PATTERN =
    /^linear-gradient\((135deg|90deg),\s*#[0-9a-fA-F]{6}\s+0%,\s*#[0-9a-fA-F]{6}\s+100%\)$/;

/**
 * Validates if an icon ID is a safe string format.
 * Only allows alphanumeric characters, hyphens, and underscores.
 */
export function isValidIconId(iconId?: string): boolean {
    if (!iconId) return true;
    return SAFE_ICON_PATTERN.test(iconId);
}

/**
 * Validates if an icon background string is a safe CSS gradient.
 */
export function isValidIconBackground(iconBackground?: string): boolean {
    if (!iconBackground) return true;
    return SAFE_GRADIENT_PATTERN.test(iconBackground);
}

/**
 * Zod refinement for icon field validation.
 */
export function validateIconId(iconId: string | undefined, ctx: z.RefinementCtx) {
    if (!isValidIconId(iconId)) {
        ctx.addIssue({
            code: 'custom',
            message: 'Icon must contain only alphanumeric characters, hyphens, and underscores.',
        });
    }
}

/**
 * Zod refinement for icon background field validation.
 */
export function validateIconBackground(iconBackground: string | undefined, ctx: z.RefinementCtx) {
    if (!isValidIconBackground(iconBackground)) {
        ctx.addIssue({
            code: 'custom',
            message:
                'Icon background must be a valid CSS gradient in the format: linear-gradient(135deg, #RRGGBB 0%, #RRGGBB 100%)',
        });
    }
}
